import React, { ChangeEvent, useState, useEffect } from 'react';

import { Button } from '@/components';
import { SelectInput } from '@/components/select-input/SelectInput';
import { TextInput } from '@/components/text-input';
import { useTranslation } from 'react-i18next';
import {
  getPycountryCompatibleCountries,
  getPycountryCompatibleCountryName,
  getCountryStateCityCompatibleCountryName
} from '@/utils/countryFilter';

import styles from './AddressModal.module.css';

interface AdressModalProps {
  onNext: () => void;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleCountryChange: (value: string | null) => void;
  shippingAddress: TypeAny;
}

function StyledDropDown(children: TypeAny) {
  return <div className="styled-drop-down">{children}</div>;
}

function AdressModal({
  shippingAddress,
  onNext,
  handleChange,
  handleCountryChange
}: AdressModalProps) {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const pycountryCompatibleCountries = getPycountryCompatibleCountries();

    // Convert pycountry name back to country-state-city name for display
    const backendCountryName = shippingAddress.country;
    const displayCountryName = backendCountryName
      ? getCountryStateCityCompatibleCountryName(backendCountryName)
      : '';

    const country = pycountryCompatibleCountries.find((c) => c.name === displayCountryName);

    if (country) {
      setSelectedCountry(country.name);
    }
  }, [shippingAddress.country]);

  const handleSelectChange = (value: string | null) => {
    setSelectedCountry(value);
    const pycountryCompatibleName = value ? getPycountryCompatibleCountryName(value) : null;
    handleCountryChange(pycountryCompatibleName);
  };
  const { name, co, address1, door_code, city, postal_code, state } = shippingAddress;

  return (
    <div className={styles.addressModalOverlay}>
      <div className={styles.addressModal}>
        <h2 className={styles.addressModalTitle}>{t('inviteView.addressModal.title')}</h2>

        <div className={styles.addressModalBody}>
          <TextInput
            value={name}
            label={t('account.name')}
            name="name"
            required
            onChange={handleChange}
          />
          <TextInput value={co} label={t('account.co')} name="co" onChange={handleChange} />
          <TextInput
            value={address1}
            label={t('account.fullAddress')}
            name="address1"
            required
            onChange={handleChange}
          />
          <TextInput
            value={door_code}
            label={t('account.doorCode')}
            name="door_code"
            onChange={handleChange}
          />
          <TextInput
            value={city}
            label={t('account.city')}
            name="city"
            required
            onChange={handleChange}
          />
          <TextInput
            value={postal_code}
            label={t('account.postalCode')}
            name="postal_code"
            required
            onChange={handleChange}
          />
          <TextInput
            value={state}
            label={t('account.state')}
            name="state"
            onChange={handleChange}
          />

          <SelectInput
            value={selectedCountry}
            label={t('account.country')}
            options={getPycountryCompatibleCountries().map((country) => ({
              value: country.name,
              label: `${country.flag} ${country.name}`
            }))}
            required
            showSearch
            onChange={handleSelectChange}
            dropdownRender={StyledDropDown}
          />
        </div>
        <div className={styles.addressModalButton}>
          <Button
            theme="cta"
            onClick={onNext}
            disabled={!name || !address1 || !city || !postal_code || !selectedCountry}
          >
            {t('inviteView.addressModal.button')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdressModal;
